import {
  Status,
  StatusIndicator,
  StatusIndicatorSize,
} from '@amzn/stencil-react-components/status-indicator';

import { Row } from '@amzn/stencil-react-components/layout';

import type { Messages } from 'src/v2/shared/i18n/useMessages';
import { riskStatusReading } from '../logic/riskScorePresentation';
import type { RiskEngineMessageKey } from '../translations';

type RiskEngineTranslator = Messages<RiskEngineMessageKey>['t'];

interface RiskStatusIndicatorProps {
  /** The raw, display-ready status string from the service (e.g. "At Risk"). */
  readonly status: string;
  readonly t: RiskEngineTranslator;
  /** Center the indicator (box and its icon + label) within its container, e.g. a centered column. */
  readonly centered?: boolean;
}

/**
 * Stencil's StatusIndicator is a flex Row (so it ignores the cell's text-align) with a 112px
 * min-width whose icon + label sit at its left edge. Centering it needs both the outer box centered
 * and its content centered inside the box. Module-level so emotion sees one stable object.
 */
const CENTERED_STATUS_STYLE = { '& > *': { justifyContent: 'center' } } as const;

/**
 * Renders a service status string as a semantic Stencil `StatusIndicator`. Shared by the risk
 * list table and the drill-down driver table so the status → colour/label mapping lives in one
 * place; unknown statuses fall back to a neutral indicator showing the raw text. Pass `centered`
 * when it sits in a center-aligned column.
 */
export function RiskStatusIndicator({
  status,
  t,
  centered = false,
}: RiskStatusIndicatorProps): JSX.Element {
  const reading = riskStatusReading(status);
  const indicator = (
    <StatusIndicator
      status={reading?.status ?? Status.Neutral}
      size={StatusIndicatorSize.Small}
      iconAriaHidden
      messageText={reading ? t(reading.messageKey) : status}
    />
  );
  if (!centered) return indicator;
  return (
    <Row justifyContent="center" css={CENTERED_STATUS_STYLE}>
      {indicator}
    </Row>
  );
}
